import { useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  getIsLeaseDomainEnabledForPhase,
  getScreeningVendor,
  pollCreditScreeningStatus,
  pollLeaseDomainCreditScreeningStatus,
  submitCreditScreening,
  submitLeaseDomainCreditScreening,
} from "../../services/AdminDashService";
import { ScreeningVendor } from "src/types/Applicant";

const SubmitCreditScreeningModal = (props) => {
  const dispatch = useAppDispatch();
  const notification = useAppSelector(
    (state) => state.applications.notification
  );
  const {
    showModal,
    setShowModal,
    applicant,
    applicationId,
    applicationConfiguration,
    configuration,
    leaseId,
    phaseId,
  } = props;
  // PR review (Copilot): getIsLeaseDomainEnabledForPhase reads appleaseNextGenEnabledPhases off
  // the global redux Configuration, not the per-application ApplicationConfiguration
  // (applicationConfiguration, used below only for legacy's FADV/TU vendor determination) -
  // passing the latter here always evaluated false, so this modal never actually routed to LD.
  const isPhaseLeaseDomainEnabled = getIsLeaseDomainEnabledForPhase(
    configuration,
    phaseId
  );
  const screeningVendor = isPhaseLeaseDomainEnabled
    ? ScreeningVendor.TRANSUNION
    : getScreeningVendor(applicant, applicationConfiguration);
  const canSubmit =
    !!applicant && !!applicationId && (!isPhaseLeaseDomainEnabled || !!leaseId);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerText, setSpinnerText] = useState("Loading...");
  const pollCleanupRef = useRef<(() => void) | null>(null);
  const preExistingScreeningIdsRef = useRef<Set<string>>(new Set());

  // Stop any in-flight poll when the modal unmounts
  useEffect(() => {
    return () => {
      pollCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!notification) return;

    if (
      showSpinner &&
      notification.type === "INFO" &&
      notification.origin === "tu-enqueue-ack"
    ) {
      // TU 202 ACK received — keep modal open and start polling
      setSpinnerText("Checking status...");
      const cleanup = dispatch(
        isPhaseLeaseDomainEnabled
          ? pollLeaseDomainCreditScreeningStatus(
              applicationId,
              applicant.id,
              `${applicant.firstName} ${applicant.lastName}`,
              preExistingScreeningIdsRef.current
            )
          : pollCreditScreeningStatus(
              applicant.id,
              `${applicant.firstName} ${applicant.lastName}`,
              screeningVendor
            )
      );
      // dispatch returns the cleanup function from the thunk
      pollCleanupRef.current = cleanup ?? null;
      return;
    }

    if (
      notification.type === "INFO" &&
      notification.message?.includes("Credit Screening for")
    ) {
      // Terminal success (FADV path, or TU poll resolved with a non-Error record)
      window.localStorage.setItem("REFRESH_APPS", Math.random().toString());
      resetModal();
      window.scrollTo(0, 0);
    } else if (notification.type === "ERROR" && notification.message) {
      resetModal();
    }
  }, [notification]);

  const runCredit = () => {
    if (!canSubmit) return;

    setDisableSubmit(true);
    setShowSpinner(true);
    setSpinnerText("Loading...");
    pollCleanupRef.current?.();
    pollCleanupRef.current = null;

    if (isPhaseLeaseDomainEnabled) {
      preExistingScreeningIdsRef.current = new Set(
        (applicant.applicantScreening ?? []).map((s) => String(s.id))
      );
      dispatch(
        submitLeaseDomainCreditScreening(
          leaseId,
          String(applicant.id),
          `${applicant.firstName} ${applicant.lastName}`,
          applicationId,
          phaseId,
          configuration
        )
      );
      return;
    }

    dispatch(
      submitCreditScreening(
        applicationId,
        applicant.id,
        `${applicant.firstName} ${applicant.lastName}`,
        applicant,
        applicationConfiguration
      )
    );
  };

  const resetModal = () => {
    pollCleanupRef.current?.();
    pollCleanupRef.current = null;
    setShowModal(false);
    setShowSpinner(false);
    setDisableSubmit(false);
    setSpinnerText("Loading...");
  };

  return (
    <Modal
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={showModal}
      className="submit-credit-modal"
    >
      <Modal.Header className="border-bottom">
        <Modal.Title>{`Submit ${screeningVendor} Credit Screening`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="details block-labels mt-0">
          <div className="subtitle">
            {`Do you want to submit the ${screeningVendor} credit screening again?`}
          </div>
          <div className="subtitle">
            {screeningVendor === ScreeningVendor.TRANSUNION
              ? "This will re-submit screening through TransUnion."
              : "This will re-submit screening through First Advantage (FADV)."}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top">
        <Button
          className="cancel-btn"
          variant="secondary"
          onClick={() => setShowModal(false)}
          disabled={disableSubmit}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => runCredit()}
          disabled={disableSubmit || !canSubmit}
        >
          {showSpinner && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
          )}

          {showSpinner
            ? spinnerText
            : `Run ${screeningVendor} Credit Screening`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubmitCreditScreeningModal;
